import { useEffect, useRef, useState } from 'react';

const DATE_FORMAT_KEY = 'smsscheduler_date_format';

const getDefaultDateFormat = () => {
  const stored = localStorage.getItem(DATE_FORMAT_KEY);
  if (stored === 'mdy' || stored === 'dmy') {
    return stored;
  }

  const path = window.location.pathname;
  return path.includes('/en-us') ? 'mdy' : 'dmy';
};

const isValidDate = (year, month, day) => {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return false;
  }
  if (year < 1900 || year > 9999) {
    return false;
  }
  if (month < 1 || month > 12) {
    return false;
  }
  if (day < 1 || day > 31) {
    return false;
  }

  const utc = new Date(Date.UTC(year, month - 1, day));
  return (
    utc.getUTCFullYear() === year &&
    utc.getUTCMonth() === month - 1 &&
    utc.getUTCDate() === day
  );
};

const toIsoDate = (partA, partB, yearValue, format) => {
  if (partA.length !== 2 || partB.length !== 2 || yearValue.length !== 4) {
    return undefined;
  }

  const first = Number.parseInt(partA, 10);
  const second = Number.parseInt(partB, 10);
  const year = Number.parseInt(yearValue, 10);

  const month = format === 'mdy' ? first : second;
  const day = format === 'mdy' ? second : first;

  if (!isValidDate(year, month, day)) {
    return undefined;
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const fromIsoDate = (isoDate, format) => {
  const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(isoDate || '');
  if (!match) {
    return { partA: '', partB: '', year: '' };
  }

  const year = match[1];
  const month = match[2];
  const day = match[3];

  return format === 'mdy'
    ? { partA: month, partB: day, year }
    : { partA: day, partB: month, year };
};

export function DayInput({
  trads,
  onChange,
  defaultValue,
  hideFormatToggle = false,
  disabled = false,
}) {
  const [dateFormat, setDateFormat] = useState(getDefaultDateFormat());
  const [partA, setPartA] = useState('');
  const [partB, setPartB] = useState('');
  const [year, setYear] = useState('');

  const partARef = useRef(null);
  const partBRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    if (!defaultValue) {
      setPartA('');
      setPartB('');
      setYear('');
      return;
    }

    let isoDate = defaultValue;
    if (defaultValue instanceof Date) {
      const yyyy = defaultValue.getFullYear();
      const mm = defaultValue.getMonth() + 1;
      const dd = defaultValue.getDate();
      isoDate = `${String(yyyy).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    }

    const parsed = fromIsoDate(String(isoDate), dateFormat);
    setPartA(parsed.partA);
    setPartB(parsed.partB);
    setYear(parsed.year);
  }, [defaultValue, dateFormat]);

  const notify = (newPartA, newPartB, newYear) => {
    const isoDate = toIsoDate(newPartA, newPartB, newYear, dateFormat);
    onChange?.(isoDate);
  };

  const handlePartA = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 2);
    setPartA(value);
    notify(value, partB, year);
    if (value.length === 2) {
      partBRef.current?.focus();
    }
  };

  const handlePartB = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 2);
    setPartB(value);
    notify(partA, value, year);
    if (value.length === 2) {
      yearRef.current?.focus();
    }
  };

  const handleYear = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(value);
    notify(partA, partB, value);
  };

  const toggleDateFormat = () => {
    const newFormat = dateFormat === 'mdy' ? 'dmy' : 'mdy';
    setDateFormat(newFormat);
    localStorage.setItem(DATE_FORMAT_KEY, newFormat);
    setPartA(partB);
    setPartB(partA);
    notify(partB, partA, year);
  };

  const firstPlaceholder = dateFormat === 'mdy' ? 'MM' : 'DD';
  const secondPlaceholder = dateFormat === 'mdy' ? 'DD' : 'MM';

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center border border-gray-300 rounded-lg px-3 h-10 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
        <input
          ref={partARef}
          type="text"
          inputMode="numeric"
          maxLength={2}
          placeholder={firstPlaceholder}
          value={partA}
          onChange={handlePartA}
          disabled={disabled}
          data-testid="date-part-a-input"
          className="w-8 text-center outline-none bg-transparent text-lg font-mono disabled:text-gray-400"
        />
        <span className="text-gray-400 text-lg font-mono">/</span>
        <input
          ref={partBRef}
          type="text"
          inputMode="numeric"
          maxLength={2}
          placeholder={secondPlaceholder}
          value={partB}
          onChange={handlePartB}
          disabled={disabled}
          data-testid="date-part-b-input"
          className="w-8 text-center outline-none bg-transparent text-lg font-mono disabled:text-gray-400"
        />
        <span className="text-gray-400 text-lg font-mono">/</span>
        <input
          ref={yearRef}
          type="text"
          inputMode="numeric"
          maxLength={4}
          placeholder="YYYY"
          value={year}
          onChange={handleYear}
          disabled={disabled}
          data-testid="date-year-input"
          className="w-14 text-center outline-none bg-transparent text-lg font-mono disabled:text-gray-400"
        />
      </div>

      {!hideFormatToggle && (
        <button
          type="button"
          onClick={toggleDateFormat}
          disabled={disabled}
          data-testid="date-format-toggle"
          className="px-3 h-10 font-medium text-gray-900 underline hover:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {dateFormat === 'mdy'
            ? trads?.index?.switchToDmy || 'Switch to DD/MM/YYYY'
            : trads?.index?.switchToMdy || 'Switch to MM/DD/YYYY'}
        </button>
      )}
    </div>
  );
}
